import { modelOptions, post, prop, PropType } from "@typegoose/typegoose";
import { z } from "zod";
import { getZodSchemaFromClass, SensorTypeString, validate } from "../../../zod-decorator/index.js";
import { SensorType } from "../../../shared.js";
import { addSchemaKeyHook, SchemaBase, getDefaultSchemaOptions, SchemaConstructorArgs } from "../../schemaBase.js";
import type {
    SproutAutomationSettingsFieldsWithDefaults,
    SproutAutomationSettingsSchemaTypes,
} from "../../types/sproutAutomationSettings.d.js";
import { type DtoValidatorTypes } from "../../validatableClass.js";
import { SensorConfigurations } from "../../../Configurations/SensorConfigurations/SensorConfiguration.js";
import { minuteMs } from "../../../timeConstants.js";

const SCHEMA_KEY = "SproutAutomationSettings" as const;

//TODO: Make these enums
const sproutAutomationModes = ["threshold", "dutyCycle", "none"] as const;
export type SproutAutomationMode = (typeof sproutAutomationModes)[number];
const sproutAutomationModeZod = z.enum(sproutAutomationModes);

const sproutAutomationSensorSelector = ["S1", "S2", "TEMP", "HUM"] as const;
export type SproutAutomationSensorSelector = (typeof sproutAutomationSensorSelector)[number];
const sproutAutomationSensorSelectorZod = z.enum(sproutAutomationSensorSelector);

@modelOptions({ schemaOptions: { ...getDefaultSchemaOptions(SCHEMA_KEY) } })
@post<SchemaBase<SproutAutomationSettings>>(...addSchemaKeyHook(SCHEMA_KEY))
export class SproutAutomationSettings extends SchemaBase<
    SproutAutomationSettings,
    SproutAutomationSettingsFieldsWithDefaults
> {
    static readonly schemaKey = SCHEMA_KEY;

    @validate({ schemaKey: SCHEMA_KEY })
    readonly schemaKey = SCHEMA_KEY;

    @validate({ zodValidator: sproutAutomationModeZod })
    @prop({
        type: () => String,
        required: true,
        validate: (v) => sproutAutomationModes.includes(v),
    })
    public automationMode!: SproutAutomationMode;

    /**
     * Which sensor is selected for sampling when automation is enabled.
     */
    @validate({ zodValidator: sproutAutomationSensorSelectorZod })
    @prop({
        type: () => String,
        required: true,
        validate: (v) => sproutAutomationSensorSelector.includes(v),
    })
    public sensorSelector!: SproutAutomationSensorSelector;

    @validate({ zodValidator: z.array(z.boolean()).optional() })
    @prop(
        {
            type: () => Boolean,
            required: true,
            default: [true, true],
        },
        PropType.ARRAY,
    )
    public automationEnabledByValveIndex: boolean[] = [true, true];

    @validate({ zodValidator: SensorTypeString })
    @prop({ type: () => String, required: true })
    public sensorType!: SensorType;

    @validate({ zodValidator: z.boolean() })
    @prop({ type: () => Boolean, required: true })
    public openValveWhenReadingBelowThreshold!: boolean;

    @validate({ zodValidator: z.array(z.number()).optional() })
    @prop({ type: () => Number, required: false }, PropType.ARRAY)
    public thresholdAutomationThresholdRAW?: number[];

    @validate({ zodValidator: z.number().optional() })
    @prop({ type: () => Number, required: false })
    public dutyCycleAutomationTargetRAW?: number;

    @validate({ zodValidator: z.number().optional() })
    @prop({ type: () => Number, required: false })
    public dutyCycleAutomationPeriod?: number;

    @validate({ zodValidator: z.number().optional() })
    @prop({ type: () => Number, required: false })
    public dutyCycleAutomationDutyCycle?: number;

    @validate({ zodValidator: z.number().optional() })
    @prop({ type: () => Number, required: false })
    public automationSensingPeriod?: number;

    static getters = {} as const satisfies Record<string, (self: SproutAutomationSettings) => any>;

    static utilities = {
        validate: (
            automationSettings: SproutAutomationSettings,
        ): { sproutAutomationSettingsValid: boolean; reason?: string } => {
            function isNotNumber(v: any): boolean {
                return isNaN(v) || !(typeof v === "number" || v instanceof Number);
            }

            function roundToTick(v: number, tickSize: number): number {
                return Math.round(v / tickSize) * tickSize;
            }

            if (!sproutAutomationModes.includes(automationSettings.automationMode)) {
                return {
                    sproutAutomationSettingsValid: false,
                    reason: `Invalid automation mode "${automationSettings.automationMode}"`,
                };
            }

            if (automationSettings.automationMode === "none") {
                return {
                    sproutAutomationSettingsValid: true,
                };
            }

            if (!sproutAutomationSensorSelector.includes(automationSettings.sensorSelector)) {
                return {
                    sproutAutomationSettingsValid: false,
                    reason: `Invalid sensor selector "${automationSettings.sensorSelector}"`,
                };
            }

            if (!automationSettings.sensorType || !SensorConfigurations[automationSettings.sensorType]) {
                return {
                    sproutAutomationSettingsValid: false,
                    reason: `"${automationSettings.sensorType}" not valid sensor type for automation`,
                };
            }

            if (automationSettings.automationMode === "threshold") {
                if (["TEMP", "HUM"].includes(automationSettings.sensorSelector)) {
                    return {
                        sproutAutomationSettingsValid: false,
                        reason: `"${automationSettings.sensorSelector}" not valid sensor selector for threshold automation`,
                    };
                }
                if (
                    !Array.isArray(automationSettings.thresholdAutomationThresholdRAW) ||
                    automationSettings.thresholdAutomationThresholdRAW.length !== 2
                ) {
                    return {
                        sproutAutomationSettingsValid: false,
                        reason: `invalid range for threshold automation`,
                    };
                }
                if (automationSettings.thresholdAutomationThresholdRAW.some((v) => isNotNumber(v))) {
                    return {
                        sproutAutomationSettingsValid: false,
                        reason: `threshold automation range has non numbers in it`,
                    };
                }
            }

            if (automationSettings.automationMode === "dutyCycle") {
                if (isNotNumber(automationSettings.dutyCycleAutomationTargetRAW)) {
                    return {
                        sproutAutomationSettingsValid: false,
                        reason: `duty cycle target ${automationSettings.dutyCycleAutomationTargetRAW} is not a number`,
                    };
                }
                if (isNotNumber(automationSettings.dutyCycleAutomationPeriod)) {
                    return {
                        sproutAutomationSettingsValid: false,
                        reason: `duty cycle period ${automationSettings.dutyCycleAutomationPeriod} is not a number`,
                    };
                }
                if (isNotNumber(automationSettings.dutyCycleAutomationDutyCycle)) {
                    return {
                        sproutAutomationSettingsValid: false,
                        reason: `duty cycle duty cycle ${automationSettings.dutyCycleAutomationDutyCycle} is not a number`,
                    };
                }
                // Validate that the period is at least 5 minutes and a multiple of 5 minutes
                if (
                    Math.abs(
                        roundToTick(automationSettings.dutyCycleAutomationPeriod, 5 * minuteMs) -
                            automationSettings.dutyCycleAutomationPeriod,
                    ) >
                    minuteMs * 0.1
                ) {
                    return {
                        sproutAutomationSettingsValid: false,
                        reason: `duty cycle period must be a multiple of 5 minutes. Got ${automationSettings.dutyCycleAutomationPeriod} (${automationSettings.dutyCycleAutomationPeriod / minuteMs} minutes)`,
                    };
                }
                if (
                    Math.abs(
                        roundToTick(automationSettings.dutyCycleAutomationDutyCycle, 0.01) -
                            automationSettings.dutyCycleAutomationDutyCycle,
                    ) > 0.001
                ) {
                    return {
                        sproutAutomationSettingsValid: false,
                        reason: `duty cycle must be a multiple of 1%. Got ${automationSettings.dutyCycleAutomationDutyCycle} (${automationSettings.dutyCycleAutomationDutyCycle}%)`,
                    };
                }
            }

            return {
                sproutAutomationSettingsValid: true,
            };
        },
    } as const satisfies Record<string, (...args: any) => any>;

    static dtoValidators: DtoValidatorTypes<SproutAutomationSettingsSchemaTypes>;

    constructor(
        properties?: SchemaConstructorArgs<SproutAutomationSettings, SproutAutomationSettingsFieldsWithDefaults>,
    ) {
        super(properties);
        Object.assign(this, properties);
    }
}

SproutAutomationSettings.dtoValidators = getZodSchemaFromClass(
    SproutAutomationSettings,
) as unknown as DtoValidatorTypes<SproutAutomationSettingsSchemaTypes>;
